// src/Pages/EnvioAlerta.jsx
import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import AlertForm from "../components/Form/AlertForm";
import { useMarkers } from "../hooks/useMarkers";
import { useGeolocation } from "../hooks/useGeolocation";
import useApi from "../hooks/useApi";
import useCategorias from "../hooks/useCategorias";
import useVias from "../hooks/useVias";
import { BusquedaVia } from "../components/Ubicacion";
import "../styles/ticketSuccess.css";

const EnvioAlerta = () => {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [categoriaId, setCategoriaId] = useState(null);
  const [viaId, setViaId] = useState(null);
  const [documento, setDocumento] = useState("");
  const [referencia, setReferencia] = useState("");
  const [showWelcome, setShowWelcome] = useState(true);
  const [formKey, setFormKey] = useState(0);
  const [modoUbicacion, setModoUbicacion] = useState('mapa'); // 'mapa' o 'via'
  const [viaSeleccionada, setViaSeleccionada] = useState(null);
  
  const { markers, setMarkers } = useMarkers(documento);
  const { latitude, setLatitude, longitude, setLongitude, address, setAddress, distrito, ciudad, fetchAddress } = useGeolocation();
  const { enviarDatos, loading } = useApi();
  const { categorias, loading: categoriasLoading } = useCategorias();
  const { vias, loading: viasLoading } = useVias();

  // Solicita el documento al usuario con diseño mejorado
  const promptForDocumento = async () => {
    const { value: documentoInput, isDismissed } = await Swal.fire({
      title: '<strong>¡Bienvenido!</strong>',
      html: `
        <div style="text-align: center; padding: 20px;">
          <div style="font-size: 60px; color: #007bff; margin-bottom: 20px;">
            🛡️
          </div>
          <h3 style="color: #333; margin-bottom: 15px;">REPORTE DE TRÁNSITO MIRAFLORES</h3>
          <p style="color: #666; margin-bottom: 20px;">
            Para enviar tu reporte, necesitamos tu número de DNI
          </p>
          <input 
            id="dni-input" 
            type="text" 
            placeholder="Ingresa tu DNI (8 dígitos)"
            style="
              width: 100%;
              padding: 15px;
              border: 2px solid #ddd;
              border-radius: 10px;
              font-size: 16px;
              text-align: center;
              margin-bottom: 15px;
            "
          />
          <div style="font-size: 12px; color: #888;">
            O puedes continuar de forma anónima
          </div>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Continuar Anónimo',
      confirmButtonText: 'Continuar con DNI',
      customClass: {
        popup: 'swal-popup-custom',
        confirmButton: 'swal-confirm-custom',
        cancelButton: 'swal-cancel-custom'
      },
      allowOutsideClick: false,
      allowEscapeKey: false,
      preConfirm: () => {
        const dni = document.getElementById('dni-input').value;
        if (dni && dni.length !== 8) {
          Swal.showValidationMessage('El DNI debe tener 8 dígitos');
          return false;
        }
        return dni;
      }
    });

    if (!isDismissed) {
      setDocumento(documentoInput || "ANONIMO");
      setShowWelcome(false);
    } else {
      // Si cancela, mostrar como anónimo
      setDocumento("ANONIMO");
      setShowWelcome(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "categoriaId") {
      setCategoriaId(parseInt(value));
    } else if (name === "referencia") {
      setReferencia(value);
    }
  };

  // Manejar selección de vía
  const handleViaSeleccionada = (via, viaId) => {
    setViaSeleccionada(via);
    setViaId(viaId);
    setModoUbicacion('via');
    
    // Establecer las coordenadas de la vía seleccionada
    if (via && via.via_deLatitud && via.via_deLongitud) {
      setLatitude(parseFloat(via.via_deLatitud));
      setLongitude(parseFloat(via.via_deLongitud));
      
      // Establecer la dirección (usar dirección completa si está disponible)
      if (via.direccionCompleta) {
        setAddress(via.direccionCompleta);
      } else {
        const direccion = `${via.tipo_via_display} ${via.via_chNombre} Cuadra ${via.via_inCuadra}`;
        setAddress(direccion);
      }
    } else {
      // Limpiar coordenadas si no hay vía válida
      setLatitude(null);
      setLongitude(null);
      setAddress('');
    }
  };

  // Cambiar modo de ubicación
  const cambiarModoUbicacion = (modo) => {
    setModoUbicacion(modo);
    if (modo === 'mapa') {
      setViaSeleccionada(null);
      setViaId(null);
      // No limpiar coordenadas aquí, ya que pueden venir del mapa
    } else if (modo === 'via') {
      // Limpiar coordenadas del mapa cuando se cambia a modo vía
      setLatitude(null);
      setLongitude(null);
      setAddress('');
    }
  };

  const handleEnviarDatos = async (formData) => {
    // Validaciones
    if (!categoriaId) {
      await Swal.fire({
        title: "Campo Requerido",
        text: "Debes seleccionar el tipo de incidencia para continuar",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: '#2c3e50',
        customClass: {
          popup: 'swal-popup-professional',
          confirmButton: 'swal-confirm-professional'
        }
      });
      return;
    }

    // Validar ubicación según el modo seleccionado
    if (modoUbicacion === 'mapa') {
      if (!latitude || !longitude) {
        await Swal.fire({
          title: "Ubicación Requerida",
          text: "Por favor selecciona la ubicación del incidente en el mapa",
          icon: "warning",
          confirmButtonText: "Entendido",
          confirmButtonColor: '#2c3e50',
          customClass: {
            popup: 'swal-popup-professional',
            confirmButton: 'swal-confirm-professional'
          }
        });
        return;
      }
    } else if (modoUbicacion === 'via') {
      if (!viaSeleccionada) {
        await Swal.fire({
          title: "Vía Requerida",
          text: "Por favor selecciona una vía específica de la lista",
          icon: "warning",
          confirmButtonText: "Entendido",
          confirmButtonColor: '#2c3e50',
          customClass: {
            popup: 'swal-popup-professional',
            confirmButton: 'swal-confirm-professional'
          }
        });
        return;
      }
    }

    if (!formData.fecha || !formData.hora) {
      await Swal.fire({
        title: "Fecha y Hora Requeridas",
        text: "Por favor completa la fecha y hora del incidente",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: '#2c3e50',
        customClass: {
          popup: 'swal-popup-professional',
          confirmButton: 'swal-confirm-professional'
        }
      });
      return;
    }

    // Validar que si no es anónimo, tenga los campos requeridos
    if (!formData.isAnonymous && documento) {
      if (!formData.apellidoPaterno || !formData.apellidoMaterno || !formData.nombres) {
        await Swal.fire({
          title: "Datos Personales Incompletos",
          text: "Para enviar con tus datos, debes completar: apellido paterno, materno y nombres",
          icon: "warning",
          confirmButtonText: "Entendido",
          confirmButtonColor: '#2c3e50',
          customClass: {
            popup: 'swal-popup-professional',
            confirmButton: 'swal-confirm-professional'
          }
        });
        return;
      }
    }

    // Validar alias obligatorio si es anónimo
    if (formData.isAnonymous) {
      if (!formData.alias || formData.alias.trim() === "") {
        await Swal.fire({
          title: "Alias Requerido",
          text: "Para enviar de forma anónima, debes ingresar un alias",
          icon: "warning",
          confirmButtonText: "Entendido",
          confirmButtonColor: '#2c3e50',
          customClass: {
            popup: 'swal-popup-professional',
            confirmButton: 'swal-confirm-professional'
          }
        });
        return;
      }
    }

    // Preparar datos para envío según el modo de ubicación
    let dataToSend;
    
    if (modoUbicacion === 'mapa') {
      dataToSend = {
        latitud: latitude,
        longitud: longitude,
        direccion: address,
        distrito: distrito, // Distrito extraído automáticamente desde coordenadas
        ciudad: ciudad,     // Ciudad extraído automáticamente desde coordenadas
        referencia: referencia,
        fk_via_id: null, // No se seleccionó vía específica
        categoriaId: categoriaId,
        documento: formData.isAnonymous ? null : documento,
        nombres: formData.isAnonymous ? null : formData.nombres, // Solo nombres si NO es anónimo
        alias: formData.isAnonymous ? formData.alias : null, // Solo alias si ES anónimo
        apellidoPaterno: formData.isAnonymous ? null : formData.apellidoPaterno,
        apellidoMaterno: formData.isAnonymous ? null : formData.apellidoMaterno,
        genero: formData.isAnonymous ? null : formData.genero,
        telefono: formData.telefono,
        email: formData.email,
        fecha: formData.fecha,
        hora: formData.hora,
        fechaFin: formData.fechaFin,
        horaFin: formData.horaFin,
        comentario: formData.comentario,
        lunes: formData.lunes,
        martes: formData.martes,
        miercoles: formData.miercoles,
        jueves: formData.jueves,
        viernes: formData.viernes,
        sabado: formData.sabado,
        domingo: formData.domingo,
        foto: formData.foto,
        mediaFiles: formData.mediaFiles,
        isAnonymous: formData.isAnonymous, // Flag para determinar endpoint
      };
    } else if (modoUbicacion === 'via') {
      dataToSend = {
        latitud: viaSeleccionada.via_deLatitud,
        longitud: viaSeleccionada.via_deLongitud,
        direccion: `${viaSeleccionada.tipo_via_display} ${viaSeleccionada.via_chNombre} Cuadra ${viaSeleccionada.via_inCuadra}${referencia ? `, ${referencia}` : ''}`,
        distrito: null, // Se obtendrá del backend
        ciudad: null,   // Se obtendrá del backend
        referencia: referencia,
        fk_via_id: viaId, // ID de la vía seleccionada
        categoriaId: categoriaId,
        documento: formData.isAnonymous ? null : documento,
        nombres: formData.isAnonymous ? null : formData.nombres, // Solo nombres si NO es anónimo
        alias: formData.isAnonymous ? formData.alias : null, // Solo alias si ES anónimo
        apellidoPaterno: formData.isAnonymous ? null : formData.apellidoPaterno,
        apellidoMaterno: formData.isAnonymous ? null : formData.apellidoMaterno,
        genero: formData.isAnonymous ? null : formData.genero,
        telefono: formData.telefono,
        email: formData.email,
        fecha: formData.fecha,
        hora: formData.hora,
        fechaFin: formData.fechaFin,
        horaFin: formData.horaFin,
        comentario: formData.comentario,
        lunes: formData.lunes,
        martes: formData.martes,
        miercoles: formData.miercoles,
        jueves: formData.jueves,
        viernes: formData.viernes,
        sabado: formData.sabado,
        domingo: formData.domingo,
        foto: formData.foto,
        mediaFiles: formData.mediaFiles,
        isAnonymous: formData.isAnonymous, // Flag para determinar endpoint
      };
    }

    // Si hay foto, convertir a base64 o manejar según tu API
    if (formData.foto) {
      // Aquí puedes agregar lógica para convertir la imagen
      // dataToSend.foto = await convertToBase64(formData.foto);
    }

    try {
      const result = await enviarDatos(dataToSend);
      
      if (result.success) {
        // Mostrar éxito con ticket del backend
        await showSuccessWithTicket(result.ticket, dataToSend);
        
        // La limpieza se hace automáticamente en showSuccessWithTicket
      }
      
    } catch (error) {
      console.error("Error al enviar datos:", error);
    }
  };

  const generateTicketNumber = () => {
    const now = new Date();
    const year = now.getFullYear().toString().substr(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `ALT-${year}${month}${day}-${random}`;
  };

  const showSuccessWithTicket = async (ticketNumber, data) => {
    // Formatear la dirección completa con referencia
    const direccionCompleta = data.referencia 
      ? `${data.direccion} (${data.referencia})`
      : data.direccion;
    
    const result = await Swal.fire({
      title: '<strong>Reporte Enviado Exitosamente</strong>',
      html: `
        <div style="text-align: center; padding: 20px;">
          <div class="ticket-container">
            <h3 style="margin-bottom: 15px; font-size: 18px; font-weight: 600; color: white;">Número de Ticket</h3>
            <div class="ticket-number">
              ${ticketNumber}
            </div>
          </div>
          <div class="ticket-details">
            <p><strong>Tipo:</strong> ${Array.isArray(categorias) ? categorias.find(cat => cat.cat_inId === data.categoriaId)?.cat_chNombre || 'No especificado' : 'No especificado'}</p>
            <p><strong>Fecha:</strong> ${data.fecha} - ${data.hora}</p>
            <div class="location-info">
              <strong>Ubicación:</strong><br/>
              ${direccionCompleta}
            </div>
            <div class="ticket-footer">
              * Conserva este número para hacer seguimiento de tu reporte
            </div>
          </div>
        </div>
      `,
      icon: 'success',
      showCancelButton: true,
      confirmButtonText: 'Copiar Ticket',
      cancelButtonText: 'Salir',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      customClass: {
        popup: 'ticket-success-modal swal-popup-professional',
        confirmButton: 'swal-confirm-copy',
        cancelButton: 'swal-cancel-exit'
      },
      allowOutsideClick: false,
      reverseButtons: true
    });

    // Si el usuario hace clic en "Copiar Ticket"
    if (result.isConfirmed) {
      try {
        await navigator.clipboard.writeText(ticketNumber);
        await Swal.fire({
          title: '¡Ticket Copiado!',
          text: 'El número de ticket ha sido copiado al portapapeles',
          icon: 'success',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#28a745',
          timer: 2000,
          timerProgressBar: true
        });
        // Reiniciar formulario después de copiar
        resetForm();
      } catch (err) {
        // Fallback para navegadores que no soportan clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = ticketNumber;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        await Swal.fire({
          title: '¡Ticket Copiado!',
          text: 'El número de ticket ha sido copiado al portapapeles',
          icon: 'success',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#28a745',
          timer: 2000,
          timerProgressBar: true
        });
        // Reiniciar formulario después de copiar (fallback)
        resetForm();
      }
    }
    
    // Si el usuario hace clic en "Salir" o cierra el modal
    if (result.dismiss === Swal.DismissReason.cancel || result.dismiss === Swal.DismissReason.close) {
      resetForm();
    }
  };

  const resetForm = () => {
    setSelectedMarker(null);
    setCategoriaId(null);
    setReferencia("");
    setLatitude(null);
    setLongitude(null);
    setDocumento("");
    setShowWelcome(true);
    setFormKey((k) => k + 1); // Forzar remount de AlertForm
    // Limpiar también el estado del formulario en AlertForm
    setTimeout(() => {
      promptForDocumento();
    }, 100);
  };

  const handleAddMarker = (lat, lng) => {
    const newMarker = {
      id: Date.now(), // Usar timestamp en lugar de markers.length + 1
      position: { lat, lng },
      categoriaId: categoriaId,
      estado: 1
    };
    
    const updatedMarkers = [...markers, newMarker];
    setMarkers(updatedMarkers);
    setLatitude(lat);
    setLongitude(lng);
    fetchAddress(lat, lng);
  };

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
    setLatitude(marker.position.lat);
    setLongitude(marker.position.lng);
    fetchAddress(marker.position.lat, marker.position.lng);
  };

  // Efecto para solicitar documento al cargar
  useEffect(() => {
    if (showWelcome) {
      promptForDocumento();
    }
  }, [showWelcome]);

  // Efecto para agregar estilos personalizados de SweetAlert2
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .swal-popup-professional {
        border-radius: 15px !important;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3) !important;
      }
      .swal-confirm-professional {
        background: linear-gradient(135deg, #2c3e50, #34495e) !important;
        border: none !important;
        border-radius: 8px !important;
        padding: 12px 24px !important;
        font-weight: 600 !important;
        font-size: 14px !important;
        transition: all 0.3s ease !important;
      }
      .swal-confirm-professional:hover {
        transform: translateY(-1px) !important;
        box-shadow: 0 4px 12px rgba(44, 62, 80, 0.3) !important;
      }
      .swal-confirm-copy {
        background: linear-gradient(135deg, #28a745, #20c997) !important;
        border: none !important;
        border-radius: 8px !important;
        padding: 12px 24px !important;
        font-weight: 600 !important;
        font-size: 14px !important;
        transition: all 0.3s ease !important;
      }
      .swal-confirm-copy:hover {
        transform: translateY(-1px) !important;
        box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3) !important;
      }
      .swal-cancel-exit {
        background: linear-gradient(135deg, #6c757d, #545b62) !important;
        border: none !important;
        border-radius: 8px !important;
        padding: 12px 24px !important;
        font-weight: 600 !important;
        font-size: 14px !important;
        transition: all 0.3s ease !important;
      }
      .swal-cancel-exit:hover {
        transform: translateY(-1px) !important;
        box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3) !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (showWelcome) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner"></div>
          <p>Cargando sistema de alertas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="envio-alerta-page">
      <div className="container">
        <AlertForm
          key={formKey}
          latitude={latitude}
          longitude={longitude}
          address={address}
          setAddress={setAddress}
          referencia={referencia}
          categorias={categorias}
          categoriaId={categoriaId}
          vias={vias}
          viaId={viaId}
          documento={documento}
          handleChange={handleChange}
          enviarDatos={handleEnviarDatos}
          markers={markers}
          handleMarkerClick={handleMarkerClick}
          handleAddMarker={handleAddMarker}
          loading={loading || categoriasLoading}
          modoUbicacion={modoUbicacion}
          cambiarModoUbicacion={cambiarModoUbicacion}
          viaSeleccionada={viaSeleccionada}
          onViaSeleccionada={handleViaSeleccionada}
        />
      </div>
    </div>
  );
};

export default EnvioAlerta;